import * as React from 'react';
import { IconQuestionMark } from '@tabler/icons';
import { cn } from '#root/lib/utils';
import { Usd, usdText } from '../../primitives/usd/usd';
import styles from './price-pair.module.scss';

export interface PricePairProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'onClick' | 'children'> {
  /** Etiqueta del precio ("Precio real", "Precio referencial") */
  priceLabel: React.ReactNode;
  /** Precio en dólares */
  price: number | string | null | undefined;
  /** Etiqueta de la comisión ("Comisión neta", "Comisión propuesta") */
  commissionLabel: React.ReactNode;
  /** Comisión en dólares */
  commission: number | string | null | undefined;
  /**
   * Con `onClick` es un botón con un `?` que abre el detalle; sin él, un bloque estático.
   * Un botón necesita `aria-label` que diga qué abre; el componente le antepone los montos
   * ("Precio ref. $1,299 + Comisión prop. $85. Ver qué son…") para que el lector los anuncie.
   */
  onClick?: () => void;
  /** Ocupa todo el ancho de su contenedor, con los dos montos en los extremos */
  block?: boolean;
  /** `match`: comisión con booster (Pedidos Match), el monto en morado en vez de verde */
  tone?: 'default' | 'match';
}

/**
 * PricePair Component - Design System
 *
 * Sub-card ligero con "precio + comisión" juntos, en dólares (`Usd`). Lo comparten Pedidos
 * Match, los pedidos regulares y el drawer de oferta.
 *
 * @example
 * // Estático
 * <PricePair priceLabel="Precio referencial" price={1390} commissionLabel="Comisión propuesta" commission={100} />
 *
 * @example
 * // Clicable: abre el detalle
 * <PricePair ... onClick={openDetail} aria-label="Ver detalle de precio real y comisión neta" />
 */
const PricePair = React.forwardRef<HTMLElement, PricePairProps>(
  (
    {
      priceLabel,
      price,
      commissionLabel,
      commission,
      onClick,
      block,
      tone,
      className,
      ...props
    },
    ref
  ) => {
    const content = (
      <>
        <span className={styles.term}>
          <small>{priceLabel}</small>
          <strong>
            <Usd value={price} />
          </strong>
        </span>
        <span className={styles.plus} aria-hidden="true">
          +
        </span>
        <span className={cn(styles.term, styles.commission)}>
          <small>{commissionLabel}</small>
          <strong>
            <Usd value={commission} />
          </strong>
        </span>
      </>
    );
    const classes = cn(
      styles.pricePair,
      block && styles.block,
      onClick && styles.interactive,
      tone === 'match' && styles.match,
      className
    );

    if (onClick) {
      // El aria-label del caller dice la acción; delante van los montos, que el label solo taparía
      const { 'aria-label': action, ...buttonProps } = props;
      const amounts = [
        typeof priceLabel === 'string' && usdText(price) && `${priceLabel} ${usdText(price)}`,
        typeof commissionLabel === 'string' &&
          usdText(commission) &&
          `${commissionLabel} ${usdText(commission)}`,
      ]
        .filter(Boolean)
        .join(' + ');
      const name = [amounts, action].filter(Boolean).join('. ') || undefined;
      return (
        <button
          ref={ref as React.Ref<HTMLButtonElement>}
          type="button"
          className={classes}
          aria-haspopup="dialog"
          aria-label={name}
          onClick={onClick}
          {...(buttonProps as React.ButtonHTMLAttributes<HTMLButtonElement>)}>
          {content}
          <IconQuestionMark className={styles.help} size={14} aria-hidden="true" />
        </button>
      );
    }

    return (
      <div ref={ref as React.Ref<HTMLDivElement>} className={classes} {...props}>
        {content}
      </div>
    );
  }
);

PricePair.displayName = 'PricePair';

export { PricePair };
